import React, { useEffect, useState } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  Button,
  Row,
  Col,
  ButtonToolbar,
} from "reactstrap";
import { Form, Field } from "react-final-form";
import arrayMutators from "final-form-arrays";
import { FieldArray } from "react-final-form-arrays";
import CheckboxGroup from "@/shared/components/form/CheckboxGroup";
import renderRadioButtonField from "@/shared/components/form/RadioButton";
import axios from "axios";
import { fetchRoles } from "@/shared/apiHelper";
import {
  useDispatch,
  flashSuccess,
  flashError,
} from "@/shared/components/flash";
import { ThemeProps, RTLProps } from "@/shared/prop-types/ReducerProps";
import { connect } from "react-redux";

const copyOrMove = [
  { id: "copy_user", name: "Copy User" },
  { id: "move_user", name: "Move User" },
];

const MoveUserModal = ({
  program,
  user,
  isOpen,
  toggle,
  setTrigger,
  theme,
  rtl,
}) => {
  const dispatch = useDispatch();

  const [roles, setRoles] = useState([]);
  const [programList, setProgramList] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (program?.id) {
      getRoles();
      fetchPrograms();
    }
  }, [program]);

  const getRoles = () => {
    fetchRoles(program.organization_id, program.id).then((data) =>
      setRoles(data),
    );
  };

  const fetchPrograms = async () => {
    try {
      const response = await axios.get(
        `/organization/${program.organization_id}/program/${program.id}/top-bottom-hierarchy`,
      );
      setProgramList(response.data);
    } catch (e) {
      console.error(e);
    }
  };

  const validate = (values) => {
    const errors = {};

    if (!Array.isArray(values.roles) || values.roles.length === 0) {
      errors.roles = "Select at least one role";
    }

    if (!Array.isArray(values.program) || values.program.length === 0) {
      errors.program = "Select at least one program";
    }

    return errors;
  };

  const onSubmit = (values) => {
    let payload = {
      roles: values.roles ?? [],
      programs: values.program ?? [],
    };

    if (values.copy_or_move === "move_user") {
      payload.move_user = 1;
    } else {
      payload.copy_user = 1;
    }

    setLoading(true);

    axios
      .post(
        `/organization/${program.organization_id}/program/${program.id}/user/${user.id}/copy_move_user`,
        payload,
      )
      .then((res) => {
        if (res.status === 200) {
          flashSuccess(dispatch, "User moved/copied successfully");
          toggle();
          setTrigger(Math.floor(Date.now() / 1000));
        }
      })
      .catch((error) => {
        flashError(dispatch, error.response?.data);
      })
      .finally(() => setLoading(false));
  };

  return (
    <Modal
      className={`modal-action manager-program-modal manager-program-modal--wide modal-lg ${theme.className} ${rtl.direction}-support`}
      isOpen={isOpen}
      toggle={toggle}
    >
      <ModalHeader tag="div" className="manager-program-modal__header">
        <div className="manager-program-modal__header-row">
          <div className="manager-program-modal__header-copy">
            <h3 className="manager-program-modal__title">Copy or Move User</h3>
            <p className="manager-program-modal__subtitle">
              Choose whether to copy or move this user into other programs.
            </p>
          </div>
          <ButtonToolbar className="manager-program-modal__actions">
            <Button outline color="primary" onClick={toggle}>
              Close
            </Button>
          </ButtonToolbar>
        </div>
      </ModalHeader>
      <ModalBody className="manager-program-modal__body">
          <Form
            onSubmit={onSubmit}
            validate={validate}
            initialValues={{
              roles: user.roles ? user.roles.map((r) => r.id) : [],
              copy_or_move: "copy_user",
            }}
            mutators={{
              ...arrayMutators,
            }}
          >
            {({ handleSubmit }) => (
              <form className="form" onSubmit={handleSubmit}>
                <div className="manager-program-modal__form-head">
                  <h4 className="manager-program-modal__form-title">Transfer Settings</h4>
                  <p className="manager-program-modal__form-copy">
                    Select the destination programs and roles for this action.
                  </p>
                </div>
                <Row className="mx-auto text-left">
                  <Col>
                    {copyOrMove.map((copy) => (
                      <Field
                        name="copy_or_move"
                        component={renderRadioButtonField}
                        label={copy.name}
                        radioValue={String(copy.id)}
                        key={`${copy.id}`}
                      />
                    ))}
                  </Col>
                </Row>
                <Row className="mx-auto text-left">
                  <Col>
                    <Field name="roles">
                      {({ input, meta }) => (
                        <div className="form__form-group">
                          <span className="form__form-group-label">Roles</span>
                          <div className="form__form-group-field">
                            <div className="form__form-group-row">
                              <FieldArray
                                component={CheckboxGroup}
                                options={roles}
                                {...input}
                              />
                              {meta.touched && meta.error && (
                                <span className="form__form-group-error">
                                  {meta.error}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      )}
                    </Field>
                  </Col>
                </Row>
                <Row className="mx-auto text-left">
                  <Col>
                    <Field name="program">
                      {({ input, meta }) => (
                        <div className="form__form-group">
                          <span className="form__form-group-label">
                            Select Programs
                          </span>
                          <div className="form__form-group-field">
                            <div className="form__form-group-row">
                              <FieldArray
                                component={CheckboxGroup}
                                options={programList}
                                {...input}
                              />
                              {meta.touched && meta.error && (
                                <span className="form__form-group-error">
                                  {meta.error}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      )}
                    </Field>
                  </Col>
                </Row>
                <div className="manager-program-modal__footer-actions">
                  <Button
                    type="submit"
                    disabled={loading}
                    className="btn btn-primary"
                    color="#ffffff"
                  >
                    Save Changes
                  </Button>
                </div>
              </form>
            )}
          </Form>
      </ModalBody>
    </Modal>
  );
};

MoveUserModal.propTypes = {
  theme: ThemeProps.isRequired,
  rtl: RTLProps.isRequired,
};

export default connect((state) => ({
  theme: state.theme,
  rtl: state.rtl,
}))(MoveUserModal);
